import {Inject, Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment, CustomEnvironment, setApiOverrides} from '../../environments/environment';
import {catchError, tap} from 'rxjs/operators';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

const serialize = (obj, prefix) => {
  if (!obj || Object.keys.length === 0) { return ''; }

  const transformValue = (val) => {

    if (typeof val === 'boolean') {
      return val.toString();
    }

    // check if null or undefined
    if (!val) {
      return '';
    }

    return val;
  };

  return prefix + Object.keys(obj).map(k => `${encodeURIComponent(k)}=${transformValue(obj[k])}`).join('&');
};

@Injectable()
export class ClientService {
  private handleError: HandleError;

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService,
              @Inject(CustomEnvironment) customEnvironment: any) {
    setApiOverrides(customEnvironment);

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents: any = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });

    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('ClientService');
  }

  // create new party member
  createParty(data: any) {
    return this.http.post(`${environment.api.client}/party`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createParty', []))
      );
  }

  // relate the party
  public relateParty(parentPartyRoleId: number, childPartyRoleId: number, relationshipTypeId: number) {
    const payload = {
      parentPartyRoleId,
      childPartyRoleId,
      relationshipTypeId
    };
    return this.http.post(`${environment.api.client}/party-role/relate`, JSON.stringify(payload), httpOptions)
      .pipe(
        catchError(this.handleError('relateParty', []))
      );
  }

  public getPartyRoleParents(id: number) {
    return this.http.get(`${environment.api.client}/party-role/${id}/parents`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyRoleParents', []))
      );
  }

  // get party member
  getPartyMember(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyMember', []))
      );
  }

  getPartyRoles(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}/party-roles`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyMember', []))
      );
  }

  public getPartyRoleChildren(id: number) {
    return this.http.get(`${environment.api.client}/party-role/${id}/children`, httpOptions);
  }

  // get partyId via crumbs party role id
  getPartyId(id: number) {
    return this.http.get(`${environment.api.client}/party/party-role/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyId', []))
      );
  }

  listByPartyId(id: number) {
    return this.http.get(`${environment.api.client}/party/search/partyid?partyId=${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('listByPartyId', []))
      );
  }

  getPartyAddresses(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}/addresses`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyAddresses', []))
      );
  }

  getPartyEmailAddresses(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}/emails`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyEmailAddresses', []))
      );
  }

  getPartyPhoneList(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}/phones`, httpOptions)
      .pipe(
        catchError(this.handleError('getPartyPhoneList', []))
      );
  }

  getTrustee(id: number) {
    return this.http.get(`${environment.api.client}/party/${id}/trustees`, httpOptions)
      .pipe(
        catchError(this.handleError('getTrustee', []))
      );
  }

  // create new party role
  createPartyRole(data: any) {
    return this.http.post(`${environment.api.client}/party/party-role`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartyRole', []))
      );
  }

  // relate the party role to the party member
  createPartyMemberPartyRoleRelationship(data: any) {
    return this.http.post(`${environment.api.client}/party-role/relate`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartyMemberPartyRoleRelationship', []))
      );
  }

  // create new address from manual entry
  createNewAddress(data: any) {
    return this.http.post(`${environment.api.client}/address`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createNewAddress', []))
      );
  }

  // create address record against the party member
  createPartyMemberAddress(data: any) {
    return this.http.post(`${environment.api.client}/party/address`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartyMemberAddress', []))
      );
  }

  // create phone record against the party member
  createPartyMemberPhone(data: any) {
    return this.http.post(`${environment.api.client}/party/phone`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartyMemberPhone', []))
      );
  }

  // create email record against the party member
  createPartyMemberEmail(data: any) {
    return this.http.post(`${environment.api.client}/party/email`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartyMemberEmail', []))
      );
  }

  getEmailTemplateById(id) {
    return this.http.get<any[]>(`${environment.api.client}/template/email/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailTemplateById', []))
      );
  }

  getEmailTemplateTags() {
    return this.http.get<any[]>(`${environment.api.client}/template/email/tags`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailTemplateTags', []))
      );
  }

  getEmailTemplates(pageSize: number = 25, pageNumber: number = 0, filter: string, partyRoleId: number, userId: number, tags: string = '') {
    return this.http.get<any[]>(`${environment.api.client}/template/email?ps=${pageSize}&p=${pageNumber}&filter=${filter}&partyRoleId=${partyRoleId}&userId=${userId}&tags=${tags}`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailTemplates', []))
      );
  }

  getEmailTemplatesByTag(pageSize: number = 25, pageNumber: number = 0, tags: string = '') {
    return this.http.get<any[]>(`${environment.api.client}/template/email?ps=${pageSize}&p=${pageNumber}&t=${tags}`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailTemplatesByTag', []))
      );
  }

  getEmailTemplateBySlug(name) {
    return this.http.get<any[]>(`${environment.api.client}/template/email?name=${name}`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailTemplateBySlug', []))
      );
  }

  getEmailById(id) {
    return this.http.get<any[]>(`${environment.api.client}/template/email/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('getEmailById', []))
      );
  }

  sendEmail(data: any) {
    const body = JSON.stringify(data);
    return this.http.post(`${environment.api.client}/comm/email`, body, httpOptions)
      .pipe(
        catchError(this.handleError('sendEmail', []))
      );
  }

  getSmsTemplateTags() {
    return this.http.get<any[]>(`${environment.api.client}/template/sms/tags`, httpOptions)
      .pipe(
        catchError(this.handleError('getSmsTemplateTags', []))
      );
  }

  getSmsTemplates(pageSize: number = 25, pageNumber: number = 1, filter: string, partyRoleId: number, userId: number) {
    return this.http.get<any[]>(`${environment.api.client}/template/sms?ps=${pageSize}&p=${pageNumber}&filter=${filter}&partyRoleId=${partyRoleId}&userId=${userId}`, httpOptions)
      .pipe(
        catchError(this.handleError('getSmsTemplates', []))
      );
  }

  getSmsTemplatesByTag(pageSize: number = 25, pageNumber: number = 0, tags: string = '') {
    return this.http.get<any[]>(`${environment.api.client}/template/sms?ps=${pageSize}&p=${pageNumber}&t=${tags}`, httpOptions)
      .pipe(
        catchError(this.handleError('getSmsTemplatesByTags', []))
      );
  }

  sendSms(data: any) {
    const body = JSON.stringify(data);
    return this.http.post(`${environment.api.client}/comm/sms`, body, httpOptions)
      .pipe(
        catchError(this.handleError('sendEmail', []))
      );
  }

  sendMessage(data: any) {
    const body = JSON.stringify(data);
    return this.http.post(`${environment.api.client}/comm/msg`, body, httpOptions)
  }

  getUsers() {
    return this.http.get<any[]>(`${environment.api.client}/users`, httpOptions)
      .pipe(
        catchError(this.handleError('getUsers', []))
      );
  }

  public getPartyLogins(id: number) {
    if (id) {
      return this.http.get(`${environment.api.client}/party/${id}/logins`, httpOptions);
    } else {
      return null;
    }

  }

  /**
   * search for party role
   * @param data ParySearchContract
   */
  public searchPartyRole(data: PartySearchContract = {}) {
    const defaults = {
      pt: null,
      prt: null,
      prc: null,
      prg: null,
      prs: null,
      name: null,
      g: false,
      oid: null,
      mineOnly: false,
      p: null,
      ps: 100,
    };

    const aux = serialize({
      ...defaults,
      ...data,
    }, '?');
    return this.http.get<any[]>(`${environment.api.client}/party/search${aux}`, httpOptions);
  }
}

export interface PartySearchContract {
  pt?: String;
  prt?: String;
  prc?: String;
  prg?: String;
  prs?: String;
  name?: String;
  g?: String;
  oid?: String;
  mineOnly?: String;
  p?: String;
  ps?: String;
}
